import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { ConfirmModal } from 'us-react-components/dist/ConfirmModal/ConfirmModal';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { AgendaList } from 'us-react-components/dist/Calendar/AgendaList';
import { BookingPreviewModal } from 'us-react-components/dist/Calendar/BookingPreviewModal';
import { CalendarToolbar } from 'us-react-components/dist/Calendar/CalendarToolbar';
import { MiniCalendar } from 'us-react-components/dist/Calendar/MiniCalendar';
import type { ViewMode } from 'us-react-components/dist/Calendar/types';
import { DayView } from 'us-react-components/dist/Calendar/views/DayView';
import { MonthView } from 'us-react-components/dist/Calendar/views/MonthView';
import { WeekView } from 'us-react-components/dist/Calendar/views/WeekView';
import {
  MONTHS,
  WEEKDAYS_LONG,
  addDays,
  addMonths,
  dateToTimeInputValue,
  dayAndTimeToDate,
  formatApiDateTime,
  fromIsoToDateKey,
  parseApiDate,
  roundToQuarter,
  startOfWeek,
  toDateKey,
} from 'us-react-components/dist/Calendar/utils';
import { ScheduleEntry, Sitter, SitterService, SitterStats } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { AvailabilityModal } from '../SitterProfile/components/Modals';

export const SitterCalendar = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [sitter, setSitter] = useState<Sitter | null>(null);
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [cursor, setCursor] = useState(() => new Date());
  const [view, setView] = useState<ViewMode>('week');
  const [bannerVisible, setBannerVisible] = useState(true);

  const [availOpen, setAvailOpen] = useState(false);
  const [editingStart, setEditingStart] = useState<string | undefined>();
  const [dayInput, setDayInput] = useState('');
  const [startTimeInput, setStartTimeInput] = useState('');
  const [endTimeInput, setEndTimeInput] = useState('');
  const [pastMidnight, setPastMidnight] = useState(false);
  const [weekly, setWeekly] = useState(false);
  const [weeklyAll, setWeeklyAll] = useState(true);
  const [weeklyUntil, setWeeklyUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  // Booking preview modal
  const [bookingOpen, setBookingOpen] = useState(false);
  const [activeBooking, setActiveBooking] = useState<ScheduleEntry['booking'] | null>(null);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [acceptsAnyTime, setAcceptsAnyTime] = useState(false);
  const [updatingSitter, setUpdatingSitter] = useState(false);
  const [removeAvailabilityConfirmOpen, setRemoveAvailabilityConfirmOpen] = useState(false);

  const [availabilityStatusOpen, setAvailabilityStatusOpen] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Calendar';
  }, []);

  const sitterId = pathId ? Number(pathId) : user?.id;
  const isSelf = !!user?.id && user.id === sitterId;

  useEffect(() => {
    if (!sitterId || !user?.id) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterId, { params: { userId: user.id, parentId: user.id } });
        const s = (resp.data?.data ?? null) as Sitter | null;
        setSitter(s);
        setAcceptsAnyTime(!!s?.acceptsAnyTime);
      } catch (err) {
        console.warn('[SitterCalendar] sitter load failed', err);
      }
    };
    void load();
  }, [sitterId, user?.id]);

  const loadSchedule = async (): Promise<void> => {
    if (!sitterId) return;
    // Load a window that comfortably covers week or month view navigation.
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const start = formatApiDateTime(new Date(year, month - 1, 1));
    const end = formatApiDateTime(new Date(year, month + 2, 1));
    try {
      const resp = await SitterService.getSchedules({
        params: { sitterId, start, end },
      });
      setSchedule((resp.data?.data ?? []) as ScheduleEntry[]);
    } catch {
      setSchedule([]);
    }
  };

  useEffect(() => {
    loadSchedule();

  }, [sitterId, cursor]);

  // ---- Navigation ----------------------------------------------------------
  const stepCursor = (delta: number): void => {
    setCursor((prev) => {
      if (view === 'month') return addMonths(prev, delta);
      if (view === 'week') return addDays(prev, 7 * delta);
      return addDays(prev, delta);
    });
  };
  const goToday = (): void => setCursor(new Date());

  const title = ((): string => {
    if (view === 'month') return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === 'week') {
      const s = startOfWeek(cursor);
      const e = addDays(s, 6);
      const sameMonth = s.getMonth() === e.getMonth();
      const left = `${MONTHS[s.getMonth()].slice(0, 3)} ${s.getDate()}`;
      const right = sameMonth
        ? `${e.getDate()}, ${e.getFullYear()}`
        : `${MONTHS[e.getMonth()].slice(0, 3)} ${e.getDate()}, ${e.getFullYear()}`;
      return `${left} – ${right}`;
    }
    return `${WEEKDAYS_LONG[cursor.getDay()]}, ${MONTHS[cursor.getMonth()]} ${cursor.getDate()}, ${cursor.getFullYear()}`;
  })();

  const getTimesForDay = (
    startDate: Date,
    excludeStart?: string,
  ): Array<{ start: string; end: string }> => {
    const key = toDateKey(startDate);
    return schedule
      .filter((e) => !e.booking && e.start !== excludeStart && fromIsoToDateKey(e.start) === key)
      .map((e) => {
        const s = parseApiDate(e.start);
        const end = parseApiDate(e.end);
        return s && end
          ? { start: formatApiDateTime(s), end: formatApiDateTime(end) }
          : null;
      })
      .filter((range): range is { start: string; end: string } => range !== null);
  };

  const openAvailability = (opts: {
    start?: Date;
    end?: Date;
    entry?: ScheduleEntry;
  }): void => {
    const { start, end, entry } = opts;
    setModalError('');
    setEditingStart(entry?.start);
    setPastMidnight(!!entry?.pastMidnight);
    setWeekly(false);
    setWeeklyAll(true);
    setWeeklyUntil('');

    let initStart: Date;
    let initEnd: Date;
    if (entry?.start && entry.end) {
      const es = parseApiDate(entry.start);
      const ee = parseApiDate(entry.end);
      initStart = roundToQuarter(es ?? new Date());
      initEnd = roundToQuarter(ee ?? new Date());
    } else if (start && end) {
      initStart = start;
      initEnd = end;
    } else if (start) {
      initStart = start;
      initEnd = new Date(start);
      initEnd.setHours(initEnd.getHours() + 4);
    } else {
      const base = new Date();
      base.setHours(18, 0, 0, 0);
      initStart = base;
      initEnd = new Date(base);
      initEnd.setHours(base.getHours() + 4);
    }
    setDayInput(toDateKey(initStart));
    setStartTimeInput(dateToTimeInputValue(initStart));
    setEndTimeInput(dateToTimeInputValue(initEnd));
    setAvailOpen(true);
  };

  const openAvailabilityForDay = (day: Date): void => {
    const base = new Date(day);
    base.setHours(18, 0, 0, 0);
    const end = new Date(base);
    end.setHours(end.getHours() + 4);
    openAvailability({ start: base, end });
  };

  const computeShowMidnight = (): boolean => {
    const s = dayAndTimeToDate(dayInput, startTimeInput);
    const e = dayAndTimeToDate(dayInput, endTimeInput);
    if (!s || !e) return false;
    return (
      (e.getHours() === 23 && e.getMinutes() >= 30) ||
      (e.getHours() === 0 && e.getMinutes() === 0) ||
      s.getHours() > e.getHours()
    );
  };

  const saveDayAvailability = async (): Promise<void> => {
    if (!sitterId) return;
    const s = dayAndTimeToDate(dayInput, startTimeInput);
    let e = dayAndTimeToDate(dayInput, endTimeInput);
    if (!s || !e) return;

    // An end at 00:00 on the same day, or an earlier end-time when the user
    // checked past-midnight, both mean "midnight of the next day".
    const sameDay = s.toDateString() === e.toDateString();
    const endIsMidnight = e.getHours() === 0 && e.getMinutes() === 0;
    if (e <= s) {
      if (pastMidnight || (sameDay && endIsMidnight)) {
        e = new Date(s);
        e.setDate(e.getDate() + 1);
        e.setHours(0, 0, 0, 0);
      } else {
        setModalError(t('pages.calendar.endBeforeStart'));
        return;
      }
    }

    setSaving(true);
    setModalError('');
    try {
      const times = getTimesForDay(s, editingStart);
      times.push({
        start: formatApiDateTime(s),
        end: formatApiDateTime(e),
      });
      const body: Record<string, unknown> = {
        sitterId,
        date: `${toDateKey(s)} 00:00:00`,
        times,
        pastMidnight: computeShowMidnight() && pastMidnight,
      };
      if (weekly) {
        if (weeklyAll) {
          const max = new Date();
          max.setMonth(max.getMonth() + 4);
          body.until = formatApiDateTime(max);
        } else if (weeklyUntil) {
          const u = dayAndTimeToDate(weeklyUntil, '00:00');
          if (u) body.until = formatApiDateTime(u);
        }
      }
      await SitterService.saveAvailabilities(body);
      toast.success(t('pages.calendar.availabilityUpdated'));
      setAvailOpen(false);
      await loadSchedule();
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { errors?: Array<{ message?: string }> } } };
      const msg = anyErr?.response?.data?.errors?.[0]?.message;
      setModalError(msg ?? t('pages.calendar.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const deleteDayBlock = async (): Promise<void> => {
    if (!sitterId) return;
    const s = dayAndTimeToDate(dayInput, startTimeInput);
    if (!s) return;
    setSaving(true);
    setModalError('');
    try {
      const times = getTimesForDay(s, editingStart);
      if (times.length === 0) {
        await SitterService.deleteAvailabilities({
          params: {
            sitterId,
            date: `${toDateKey(s)} 00:00:00`,
          },
        });
      } else {
        await SitterService.saveAvailabilities({
          sitterId,
          date: `${toDateKey(s)} 00:00:00`,
          times,
          pastMidnight: false,
        });
      }
      toast.success(t('pages.calendar.availabilityRemoved'));
      setAvailOpen(false);
      await loadSchedule();
    } catch {
      setModalError(t('pages.calendar.deleteError'));
    } finally {
      setSaving(false);
    }
  };

  // ---- Settings / misc -----------------------------------------------------
  const confirmUpToDate = async (): Promise<void> => {
    const statsId = sitter?.stats?.id;
    if (!sitterId || !statsId) return;
    try {
      const resp = await SitterService.patchStats(sitterId, statsId, {
        id: statsId,
        sitterId,
        lastCalendarUpdate: {
          last: formatApiDateTime(new Date()),
        },
      });
      const updatedStats = resp.data?.data as SitterStats | undefined;
      if (updatedStats) {
        setSitter((prev) => (prev ? { ...prev, stats: updatedStats } : prev));
      }
      toast.success(t('pages.calendar.confirmedUpToDate'));
    } catch {
      toast.error(t('pages.calendar.updateError'));
    }
  };

  const updateAcceptsAnyTime = async (val: boolean): Promise<void> => {
    if (!sitterId) return;
    setUpdatingSitter(true);
    try {
      await SitterService.patch(sitterId, {
        id: sitterId,
        acceptsAnyTime: !val,
      });
      toast.success(t('pages.calendar.requestAvailabilityUpdated'));
      setAcceptsAnyTime(val);
      setSettingsOpen(false);
    } catch {
      toast.error(t('pages.calendar.updateError'));
    } finally {
      setUpdatingSitter(false);
    }
  };

  const removeAllAvailability = async (): Promise<void> => {
    if (!sitterId) return;
    try {
      await SitterService.deleteAvailabilities({ params: { sitterId } });
      toast.success(t('pages.calendar.availabilityRemoved'));
      setSettingsOpen(false);
      await loadSchedule();
    } catch {
      toast.error(t('pages.calendar.removeError'));
      throw new Error('remove-availability-failed');
    }
  };

  const openBookingPreview = (entry: ScheduleEntry): void => {
    if (!entry.booking) return;
    setActiveBooking(entry.booking);
    setBookingOpen(true);
  };

  if (!sitter) {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  const stats = sitter.stats;
  const dayLabelForModal = ((): string => {
    const s = dayAndTimeToDate(dayInput, startTimeInput || '00:00');
    return s ? WEEKDAYS_LONG[s.getDay()] : '';
  })();

  return (
    <div className='container py-6 max-w-7xl mx-auto'>
      {isSelf && bannerVisible && (
        <div className='us-card rounded p-3 mb-4 bg-sapphire-50 flex items-start gap-3 relative'>
          <i className='fa-solid fa-calendar-days text-2xl text-sapphire mt-0.5' aria-hidden='true' />
          <p className='font-semibold mb-0 flex-1'>
            {t('pages.calendar.dragBanner')}
          </p>
          <Button
            buttonType={ButtonType.tertiary}
            aria-label={t('pages.calendar.dismiss')}
            onClick={(): void => setBannerVisible(false)}
            className='text-neutral-500 hover:text-neutral-700'
          >
            &times;
          </Button>
        </div>
      )}

      {isSelf && (
        <h2 className='text-xl font-semibold mb-3'>{t('pages.calendar.makeSureAppear')}</h2>
      )}

      {isSelf && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-4'>
          <div className='border-l-4 border-sapphire pl-3'>
            <h4 className='font-semibold mb-1'>{t('pages.calendar.occasionalJobs')}</h4>
            <Button
              id='confirm-calendar'
              buttonType={ButtonType.tertiary}
              className='underline text-sapphire text-sm'
              onClick={confirmUpToDate}
            >
              {t('pages.calendar.confirmUpToDate')}
            </Button>
            {stats?.lastCalendarUpdate?.label && (
              <div className='italic text-xs text-neutral-600'>
                {t('pages.calendar.lastUpdate', { date: stats.lastCalendarUpdate.label })}
              </div>
            )}
          </div>
          <div className='border-l-4 border-sapphire pl-3'>
            <h4 className='font-semibold mb-1'>{t('pages.calendar.fullTimePartTime')}</h4>
            <Button
              id='update-general-availability'
              buttonType={ButtonType.tertiary}
              className='underline text-sapphire text-sm !p-0'
              onClick={(): void => setAvailabilityStatusOpen(true)}
            >
              {t('pages.calendar.updateGeneralAvailability')}
            </Button>
            {stats?.generalAvailability?.label && (
              <div className='italic text-xs text-neutral-600'>
                {t('pages.calendar.lastUpdate', { date: stats.generalAvailability.label })}
              </div>
            )}
          </div>
        </div>
      )}

      <CalendarToolbar
        title={title}
        view={view}
        onView={setView}
        onPrev={(): void => stepCursor(-1)}
        onToday={goToday}
        onNext={(): void => stepCursor(1)}
        onSettings={isSelf ? (): void => setSettingsOpen(true) : undefined}
        namespace={namespaces.caregiver}
      />

      <div className='grid grid-cols-1 md:grid-cols-12 gap-4 mt-3'>
        <aside className='md:col-span-3 space-y-3'>
          {isSelf && (
            <Button
              id='add-availability-lg'
              buttonType={ButtonType.tertiary}
              onClick={(): void => openAvailability({})}
              className='w-full !rounded-lg !text-white bg-emerald hover:bg-emerald-600 py-4 text-lg font-semibold'
            >
              <span className='text-2xl mr-2 align-middle'>+</span>
              <span className='align-middle'>{t('pages.calendar.addAvailability')}</span>
            </Button>
          )}
          <MiniCalendar
            cursor={cursor}
            onSelectDate={(d): void => setCursor(d)}
            schedule={schedule}
            namespace={namespaces.caregiver}
          />
          <AgendaList
            schedule={schedule}
            onSelectDate={(d): void => {
              setCursor(d);
              if (view === 'month') setView('week');
            }}
            namespace={namespaces.caregiver}
          />
        </aside>

        <main className='md:col-span-9'>
          {view === 'week' && (
            <WeekView
              cursor={cursor}
              schedule={schedule}
              canEdit={isSelf}
              onCreate={(start, end): void => openAvailability({ start, end })}
              onEditAvailability={(entry): void => openAvailability({ entry })}
              onOpenBooking={openBookingPreview}
              namespace={namespaces.caregiver}
            />
          )}
          {view === 'day' && (
            <DayView
              cursor={cursor}
              schedule={schedule}
              canEdit={isSelf}
              onCreate={(start, end): void => openAvailability({ start, end })}
              onEditAvailability={(entry): void => openAvailability({ entry })}
              onOpenBooking={openBookingPreview}
              namespace={namespaces.caregiver}
            />
          )}
          {view === 'month' && (
            <MonthView
              cursor={cursor}
              schedule={schedule}
              canEdit={isSelf}
              onCreateOnDay={openAvailabilityForDay}
              onOpenBooking={openBookingPreview}
              onEditAvailability={(entry): void => openAvailability({ entry })}
              onJumpToDay={(d): void => {
                setCursor(d);
                setView('day');
              }}
              namespace={namespaces.caregiver}
            />
          )}
        </main>
      </div>

      {/* Availability edit modal */}
      <Modal
        isOpen={availOpen}
        onClose={(): void => setAvailOpen(false)}
        title={t('pages.calendar.availabilityModalTitle', { day: dayLabelForModal })}
        className='w-full max-w-lg'
        footer={
          <>
            <Button
              id='clear-day'
              buttonType={ButtonType.tertiary}
              disabled={saving}
              onClick={deleteDayBlock}
            >
              {t('common.delete')}
            </Button>
            <Button
              id='save-availability'
              buttonType={ButtonType.primary}
              disabled={saving}
              onClick={saveDayAvailability}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3'>
          <div>
            <label htmlFor='availability-start-date' className='block text-sm font-semibold mb-1'>
              {t('pages.calendar.fieldDate')}
            </label>
            <input
              id='availability-start-date'
              aria-label={t('pages.calendar.fieldStartDate')}
              type='date'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              value={dayInput}
              onChange={(e): void => setDayInput(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor='availability-start-time' className='block text-sm font-semibold mb-1'>
              {t('pages.calendar.fieldStartTime')}
            </label>
            <input
              id='availability-start-time'
              aria-label={t('pages.calendar.fieldStartTime')}
              type='time'
              step={900}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              value={startTimeInput}
              onChange={(e): void => setStartTimeInput(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor='availability-end-time' className='block text-sm font-semibold mb-1'>
              {t('pages.calendar.fieldEndTime')}
            </label>
            <input
              id='availability-end-time'
              aria-label={t('pages.calendar.fieldEndTime')}
              type='time'
              step={900}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              value={endTimeInput}
              onChange={(e): void => setEndTimeInput(e.target.value)}
            />
          </div>
        </div>

        {computeShowMidnight() && (
          <label className='flex items-center gap-2 mb-3'>
            <input
              id='midnight'
              type='checkbox'
              checked={pastMidnight}
              onChange={(e): void => setPastMidnight(e.target.checked)}
            />
            {t('pages.calendar.pastMidnight')}
          </label>
        )}

        <label className='flex items-center gap-2 mb-3'>
          <input
            id='weekly'
            type='checkbox'
            checked={weekly}
            onChange={(e): void => setWeekly(e.target.checked)}
          />
          {t('pages.calendar.applyToFutureDays', { day: dayLabelForModal })}
        </label>
        {weekly && (
          <div className='flex items-center gap-3 mb-3 pl-6'>
            <label className='flex items-center gap-1'>
              <input
                id='all'
                type='radio'
                name='weekly-scope'
                checked={weeklyAll}
                onChange={(): void => setWeeklyAll(true)}
              />
              {t('pages.calendar.scopeAll')}
            </label>
            <label className='flex items-center gap-1'>
              <input
                id='until'
                type='radio'
                name='weekly-scope'
                checked={!weeklyAll}
                onChange={(): void => setWeeklyAll(false)}
              />
              {t('pages.calendar.scopeUntil')}
            </label>
            {!weeklyAll && (
              <input
                id='until-date'
                aria-label={t('pages.calendar.fieldUntilDate')}
                type='date'
                className='form-control border border-neutral-300 rounded px-3 py-2'
                value={weeklyUntil}
                onChange={(e): void => setWeeklyUntil(e.target.value)}
              />
            )}
          </div>
        )}

        {modalError && <p className='text-red-600 text-sm mb-3'>{modalError}</p>}
      </Modal>

      <BookingPreviewModal
        isOpen={bookingOpen}
        onClose={(): void => setBookingOpen(false)}
        booking={activeBooking}
        namespace={namespaces.caregiver}
        showContactDetails={isSelf}
      />

      {/* Settings modal */}
      <Modal
        isOpen={settingsOpen}
        onClose={(): void => setSettingsOpen(false)}
        title={t('common.settings')}
        className='w-full max-w-md'
      >
        <label className='flex items-start gap-2 mb-4'>
          <input
            id='acceptsAnyTime'
            type='checkbox'
            checked={!acceptsAnyTime}
            disabled={updatingSitter}
            onChange={(e): void => {
              void updateAcceptsAnyTime(!e.target.checked);
            }}
          />
          <span>
            {t('pages.calendar.allowOnlyAvailability')}
          </span>
        </label>
        <hr className='my-3' />
        <Button
          id='removeAvailability'
          buttonType={ButtonType.secondary}
          className='!border-red-600 !text-red-600 hover:!bg-red-50 hover:!border-red-700 hover:!text-red-700'
          onClick={(): void => setRemoveAvailabilityConfirmOpen(true)}
        >
          {t('pages.calendar.removeAllAvailability')}
        </Button>
      </Modal>

      <ConfirmModal
        isOpen={removeAvailabilityConfirmOpen}
        onClose={(): void => setRemoveAvailabilityConfirmOpen(false)}
        title={t('pages.calendar.removeAllAvailabilityConfirm')}
        body={t('pages.calendar.removeAllAvailabilityBody')}
        confirmLabel={t('common.remove')}
        onConfirm={removeAllAvailability}
      />

      <AvailabilityModal
        isOpen={availabilityStatusOpen}
        onClose={(): void => setAvailabilityStatusOpen(false)}
        sitter={sitter}
        canEdit={isSelf}
        lastCalendarUpdateLabel={stats?.lastCalendarUpdate?.label}
        onSaved={async (): Promise<void> => {
          if (!sitterId || !user?.id) return;
          const resp = await SitterService.get(sitterId, {
            params: { userId: user.id, parentId: user.id },
          });
          const s = (resp.data?.data ?? null) as Sitter | null;
          if (s) setSitter(s);
        }}
      />
    </div>
  );
});
